// mapConfig.js - Configuraciones para Google Maps
// Centraliza todas las configuraciones del mapa en un solo archivo

// ============================
// CONFIGURACIÓN PRINCIPAL
// ============================
// src/utils/mapConfig.js
export const MAP_CONFIG = {
  // 🔑 API KEY desde variable de entorno o valor directo
  API_KEY: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'YOUR_GOOGLE_MAPS_API_KEY_HERE',
  
  DEFAULT_CENTER: { lat: 4.5709, lng: -74.2973 }, // Centro de Colombia
  DEFAULT_ZOOM: 7,
  LIBRARIES: ['geometry'],
  
  MAP_OPTIONS: {
    mapTypeId: 'roadmap',
    zoomControl: true,
    mapTypeControl: true,
    scaleControl: true,
    streetViewControl: false,
    rotateControl: false,
    fullscreenControl: true
  }
};

export const MAP_STYLES = {
  default: [
    {
      featureType: 'administrative',
      elementType: 'geometry.stroke',
      stylers: [{ color: '#c9b2a6' }]
    },
    {
      featureType: 'water',
      elementType: 'geometry.fill',
      stylers: [{ color: '#b9d3c2' }]
    },
    {
      featureType: 'landscape.natural',
      elementType: 'geometry',
      stylers: [{ color: '#dfd2ae' }]
    }
  ],
  dark: [
    { elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
    {
      featureType: 'water',
      elementType: 'geometry',
      stylers: [{ color: '#17263c' }]
    }
  ],
  minimal: [
    {
      featureType: 'administrative',
      elementType: 'labels',
      stylers: [{ visibility: 'off' }]
    },
    {
      featureType: 'poi',
      stylers: [{ visibility: 'off' }]
    }
  ],
  // En mapConfig.js, agrega a MAP_STYLES:
  administrative_borders: [
    // Forzar límites de departamentos siempre visibles
    {
      featureType: 'administrative.province',
      elementType: 'geometry.stroke',
      stylers: [
        { color: '#1976D2' },
        { weight: 1.8 },
        { visibility: 'on' }
      ]
    },
    {
      featureType: 'administrative.province',
      elementType: 'labels.text.fill',
      stylers: [
        { color: '#1976D2' },
        { visibility: 'on' }
      ]
    },
    {
      featureType: 'administrative.province',
      elementType: 'labels.text.stroke',
      stylers: [
        { color: '#FFFFFF' },
        { weight: 2 },
        { visibility: 'on' }
      ]
    },
    // País más sutil
    {
      featureType: 'administrative.country',
      elementType: 'geometry.stroke',
      stylers: [
        { color: '#757575' },
        { weight: 1 },
        { visibility: 'on' }
      ]
    },
    // Forzar todos los elementos administrativos
    {
      featureType: 'administrative',
      elementType: 'all',
      stylers: [
        { visibility: 'on' }
      ]
    }
  ]
};

export const MARKER_CONFIG = {
  COLORS: {
    NO_INICIADO: '#ef4444',
    INSCRIPCION: '#3b82f6', 
    DOCUMENTACION: '#f59e0b',
    COMPLETADO: '#22c55e',
    DEFAULT: '#6b7280'
  },
  SIZES: {
    SMALL: 8,
    MEDIUM: 12,
    LARGE: 16,
    EXTRA_LARGE: 20
  }
};

export const DEPARTMENT_CENTERS = {
  'Antioquia': { lat: 6.2442, lng: -75.5812 },
  'Atlántico': { lat: 10.9685, lng: -74.7813 },
  'Bolívar': { lat: 10.4236, lng: -75.5378 },
  'Cundinamarca': { lat: 4.7110, lng: -74.0721 },
  'Magdalena': { lat: 11.2408, lng: -74.1990 },
  'Risaralda': { lat: 4.8133, lng: -75.6961 },
  'Santander': { lat: 7.1193, lng: -73.1227 },
  'Tolima': { lat: 4.4389, lng: -75.2322 },
  'Valle del Cauca': { lat: 3.4516, lng: -76.5320 }
};

// 🔥 FUNCIONES UTILITARIAS
export const getMarkerColor = (municipality) => {
  if (municipality.workflow3_completed) return MARKER_CONFIG.COLORS.COMPLETADO;
  if (municipality.workflow2_completed) return MARKER_CONFIG.COLORS.DOCUMENTACION;
  if (municipality.workflow1_completed) return MARKER_CONFIG.COLORS.INSCRIPCION;
  return MARKER_CONFIG.COLORS.NO_INICIADO;
};

export const createCustomIcon = (color, scale = MARKER_CONFIG.SIZES.MEDIUM) => {
  if (!window.google) return null;
  
  return {
    path: window.google.maps.SymbolPath.CIRCLE,
    fillColor: color,
    fillOpacity: 0.9,
    strokeColor: '#ffffff',
    strokeWeight: 3,
    scale: scale
  };
};

export const getDepartmentCenter = (departmentName) => {
  return DEPARTMENT_CENTERS[departmentName] || MAP_CONFIG.DEFAULT_CENTER;
};

// 🚀 FUNCIÓN OPTIMIZADA DE CARGA
export const loadGoogleMapsAPI = () => {
  return new Promise((resolve, reject) => {
    // Si ya está cargado
    if (window.google && window.google.maps) {
      resolve(window.google.maps);
      return;
    }

    // Si ya existe el script
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      const checkLoaded = setInterval(() => {
        if (window.google && window.google.maps) {
          clearInterval(checkLoaded);
          resolve(window.google.maps);
        }
      }, 50);
      
      setTimeout(() => {
        clearInterval(checkLoaded);
        if (!window.google || !window.google.maps) {
          reject(new Error('Timeout cargando Google Maps'));
        }
      }, 8000);
      return;
    }

    // Verificar API key
    if (!MAP_CONFIG.API_KEY || MAP_CONFIG.API_KEY.includes('YOUR_')) {
      console.warn('⚠️ API Key no configurada, continuando en modo de prueba');
      resolve(null);
      return;
    }

    // Crear nuevo script
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${MAP_CONFIG.API_KEY}&libraries=${MAP_CONFIG.LIBRARIES.join(',')}&loading=async`;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', () => resolve(window.google.maps));
    script.addEventListener('error', reject);
    document.head.appendChild(script);
  });
};

export default MAP_CONFIG;